














































































































import Vue from 'vue'
import 'driver.js/dist/driver.min.css'
import {Component, Prop, Watch} from 'vue-property-decorator'
import Modal from '@/components/Modal.vue'
import { sendMessage } from '@/api/notices'
import { isEmptyString } from "@/utils/string"
import { noticeTypeTitle } from "@/utils/notices"
import { Action, Getter, Mutation } from 'vuex-class'
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons"
import {
    IComposer,
    IMessage,
    IRecipient
} from '@/entities/notices'
import NoticeRow from '@/components/Notices/NoticeRow.vue'
import NoticeMessage from '@/components/Notices/NoticeMessage.vue'
import NoticeSchedulerModal from '@/components/Notices/NoticeSchedulerModal.vue'
import RecipientPicker from '@/components/Notices/RecipientPicker.vue'
import NoticeTypePicker from '@/components/Notices/NoticeTypePicker.vue'
import Quote from '@/components/Notices/Quote.vue'
import DesktopRecipientPicker from "@/components/Notices/DesktopRecipientPicker.vue"
import RecipientPickerModal from "@/components/Notices/RecipientPickerModal.vue"
import SelectedRecipientPills from "@/components/Notices/SelectedRecipientPills.vue"
import ChatInput from '@/components/Chat/ChatInput.vue'
import Typography from "@/components/Rain/Typography/Typography.vue"
import BaseButton from "@/components/Rain/Buttons/BaseButton.vue"

declare const UIkit

@Component<NoticesComposer>({
    components: {
        SelectedRecipientPills,
        RecipientPickerModal,
        BaseButton,
        Typography,
        DesktopRecipientPicker,
        Quote,
        ChatInput,
        Modal,
        NoticeMessage,
        NoticeRow,
        NoticeSchedulerModal,
        NoticeTypePicker,
        RecipientPicker,
        FontAwesomeIcon
    }
})
export default class NoticesComposer extends Vue {
    @Prop(Boolean) public show!: boolean
    @Prop({type: String, default: ''}) public sendButtonTextOverride!: string
    @Prop({type: Boolean, default: true}) public canPickRecipients!: boolean
    @Prop({type: Boolean, default: true}) public canPickSchedule!: boolean
    @Prop({type: Boolean, default: false}) public resetOnShow!: boolean
    @Prop({type: String, default: 'type'}) public startStep!: string
    @Prop({type: Object, default: ()=> ({})}) public notice!: {type: string, message: string, recipients: IRecipient[], question: null | object }
    @Prop(Function) public saveOverride!: (IComposer) => any

    @Getter private recipients!: IRecipient[]
    @Getter private composer !: IComposer
    @Getter private timezone !: string

    @Action private loadRecipients
    @Action private setCanSendNotices
    @Action private setComposerMessage
    @Action private setComposerMentions
    @Action private setErrorMessage
    @Action private setComposerRecipients
    @Action private setComposerNoticeType
    @Action private setComposerScheduledAt
    @Action private setComposerQuestion
    @Action private resetComposer
    @Action private loadTimezone

    @Mutation private setMessage
    private saving = false
    private showSchedule = false
    private wordLimit = 301
    private contentLength = 0
    private composingStep = '' // ['message', 'type', 'recipient']
    private scheduledTimeLabel = 'Now'
    private selectedRecipients: IRecipient[] = []
    private modalHeight = 600
    private maxModalWidth = 600
    private faChevronDown = faChevronDown

    @Watch('show')
    onShowChange(show) {
        if (this.resetOnShow) {
            if (show) {
                //Reset recipients & question on show
                this.selectedRecipients = [...this.composer.recipients]

                if (this.notice?.question) {
                    this.setComposerQuestion(this.notice.question)
                }

                // Need to set type again as it clears on close
                this.setComposerNoticeType(this.notice.type)
            } else {
                this.showNoticeTypePicker()
            }
        }
    }

    public beforeMount() {
        this.composingStep = this.startStep
        if(this.notice) {
            // set notice type
            this.setComposerNoticeType(this.notice.type)
            this.setComposerMessage(this.notice.message)
            this.setComposerRecipients(this.notice.recipients ?? [])
            if (this.notice?.question) {
                this.setComposerQuestion(this.notice.question)
            }
        }
    }

    public async mounted() {
        this.selectedRecipients = [...this.composer.recipients]
        if (!this.timezone) {
            this.loadTimezone()
        }
    }

    private onSelectNoticeType(noticeType) {
        this.setComposerNoticeType(noticeType)
        this.composingStep = 'message'
    }

    private showRecipientPicker() {
        this.composingStep = 'recipient'
        if (!this.recipients || !this.recipients.length) {
          this.loadRecipients()
        }
    }

    private showNoticeTypePicker() {
        this.composingStep = this.startStep
    }

    private async sendNoticeMessage() {
        if (!this.canSend) {
            return
        }
        if (this.saveOverride) {
            this.saving = true
            const success = await this.saveOverride(this.composer)
            this.saving = false
            if (success) {
                // reset when success
                this.composingStep = this.startStep
                this.scheduledTimeLabel = 'Now'
                this.selectedRecipients = []
                this.resetComposer()
            }
            return
        }

        this.saving = true
        const {data} = await sendMessage(this.composer)
        this.saving = false
        this.setErrorMessage(data.msg)
        if (data.success) {
            data.scheduled_at = this.composer.scheduled_at
            this.composingStep = 'type' //reset display
            this.scheduledTimeLabel = 'Now'
            this.selectedRecipients = []
            const sentType = this.composer.scheduled_at ? 'scheduled' : 'sent'
            UIkit.notify(data.msg || `Notice ${ sentType }`, {status: 'success', pos: 'top-right'})
            this.resetComposer()
            this.$emit('notice-sent', data)
        } else {
            UIkit.notify(data.msg, {
                status: 'warning',
                pos: 'top-right'
            })
        }
    }

    private get editingMessage() {
        return this.composer.message
    }

    private get canSend(): boolean {
        return !!(this.editingMessage
            && !isEmptyString(this.editingMessage)
            && this.contentLength <= this.wordLimit
            && (!this.canPickRecipients || (this.canPickRecipients && this.hasRecipients))
            && !this.saving)
    }

    private get sendButtonLabel(): string {
        if (this.sendButtonTextOverride) {
           return this.sendButtonTextOverride
        }
        return this.composer.scheduled_at === '' ? 'Send' : 'Schedule'
    }

    private get hasRecipients() {
        return this.selectedRecipients.length > 0
    }

    private get typeTitle() {
        return noticeTypeTitle(this.composer.type)
    }

    private get composerRecipients() {
        return this.composer.recipients
    }

    private onMessageUpdate(text: string, length: number) {
        this.contentLength = length
        this.setComposerMessage(text)
        // also update mentions in case one mention is deleted
        const allMentions = this.recipients.filter(mention =>
            this.composer.message.includes(`@${mention.name} `)
        )
        this.setComposerMentions(allMentions)
    }

    private onClickCreate() {
        if (this.composer.type) {
            this.composingStep = 'message'
        } else {
            this.composingStep = 'type'
        }
    }

    private async onClickMessage(message: IMessage, type: string) {
        this.setMessage(message)
    }

    private async onClose() {
        this.setComposerNoticeType('type')
        this.$emit('close')
    }

    private onScheduleTime(timeStr, label) {
        this.setComposerScheduledAt(timeStr)
        this.scheduledTimeLabel = label
    }

    private onToggleRecipient(selectedRecipients: IRecipient[]) {
        this.selectedRecipients = selectedRecipients
    }

    private onCancelRecipients() {
        this.composingStep = 'message'
        this.selectedRecipients = this.composer.recipients
    }

    private onConfirmRecipients(confirmedRecipients: IRecipient[]) {
        this.composingStep = 'message'
        this.setComposerRecipients(confirmedRecipients)
        this.selectedRecipients = confirmedRecipients
    }

    public scrollToEditor(isClamped) {
        if (isClamped) {
            const el = this.$refs['notice-composer-editor'] as HTMLDivElement
            el.scrollTop = 0;
        }
    }
}
